import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    Briefcase,
    MapPin,
    Loader,
} from "lucide-react";
import {
    getManagerMissionInboxRequest,
    managerMissionDecisionRequest,
    getManagerMissionHistoryRequest,
} from "../../../../redux/actions/missionRequestActions";
import "../TraitementStyles/TraitementAvances.css";

const TraitementMissionsManager = () => {
    const dispatch = useDispatch();

    const { managerInbox = [], managerHistory = [], loading } = useSelector(
        (state) => state.missionRequests
    );

    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const [filters, setFilters] = useState({
        workflow: "",
        lieu: "",
        dateDebut: "",
        dateFin: "",
    });

    useEffect(() => {
        dispatch(getManagerMissionInboxRequest());
        dispatch(getManagerMissionHistoryRequest());
    }, [dispatch]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_MANAGER: { label: "En attente", bg: "#fff3cd", color: "#856404" },
            PENDING_ADMIN:   { label: "Transmis à la RH", bg: "#d1ecf1", color: "#0c5460" },
            APPROVED:        { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED:        { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
            CANCELLED:       { label: "Annulé", bg: "#e2e8f0", color: "#4b5563" },
        };
        const c = config[statut] || config.PENDING_MANAGER;
        return (
            <span className="tavBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const handleApprove = (id) => {
        dispatch(managerMissionDecisionRequest({ id, decision: true, comment: "" }));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedDemande) return;
        dispatch(
            managerMissionDecisionRequest({
                id: selectedDemande.id,
                decision: false,
                comment: motifRejet.trim(),
            })
        );
        closeReject();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => setShowDetailsModal(false);

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    const mapStatutToTab = (statut) => {
        if (statut === "PENDING_MANAGER") return "enCours";
        if (statut === "PENDING_ADMIN" || statut === "APPROVED") return "approuve";
        if (statut === "REJECTED") return "rejete";
        return "enCours";
    };

    const allDemandes = useMemo(
        () => [...managerInbox, ...managerHistory],
        [managerInbox, managerHistory]
    );

    const filteredDemandes = useMemo(() => {
        let list = allDemandes.filter((d) => mapStatutToTab(d.status) === activeTab);

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(
                (d) =>
                    d.employeeName?.toLowerCase().includes(term) ||
                    d.employeeMatricule?.toLowerCase().includes(term) ||
                    d.ordreMissionLibelle?.toLowerCase().includes(term) ||
                    d.ordreMissionCode?.toLowerCase().includes(term)
            );
        }

        if (filters.workflow) {
            list = list.filter((d) => d.ordreMissionWorkflow === filters.workflow);
        }
        if (filters.lieu) {
            list = list.filter((d) =>
                d.ordreMissionLieu?.toLowerCase().includes(filters.lieu.toLowerCase())
            );
        }
        if (filters.dateDebut) {
            list = list.filter(
                (d) => new Date(d.createdAt).toISOString().split("T")[0] >= filters.dateDebut
            );
        }
        if (filters.dateFin) {
            list = list.filter(
                (d) => new Date(d.createdAt).toISOString().split("T")[0] <= filters.dateFin
            );
        }

        return list;
    }, [allDemandes, activeTab, searchTerm, filters]);

    const tabs = useMemo(() => [
        {
            id: "enCours",
            label: "En cours",
            count: allDemandes.filter((d) => d.status === "PENDING_MANAGER").length,
        },
        {
            id: "approuve",
            label: "Approuvées",
            count: allDemandes.filter((d) => d.status === "APPROVED" || d.status === "PENDING_ADMIN").length,
        },
        {
            id: "rejete",
            label: "Rejetées",
            count: allDemandes.filter((d) => d.status === "REJECTED").length,
        },
    ], [allDemandes]);

    const workflows = useMemo(() => {
        return [...new Set(allDemandes.map((d) => d.ordreMissionWorkflow).filter(Boolean))];
    }, [allDemandes]);

    if (loading && allDemandes.length === 0) {
        return (
            <div className="tavPage">
                <div className="tavWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tavPage">
            <div className="tavWrap">
                {/* Header */}
                <div className="tavHeader">
                    <div>
                        <h1>Traitement des demandes de mission</h1>
                        <p>Gérez et validez les demandes de mission de vos collaborateurs</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tavCard">
                    <div className="tavControls">
                        <div className="tavSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, code mission..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button
                            type="button"
                            className="tavFilterBtn"
                            onClick={() => setShowFilters((v) => !v)}
                        >
                            <Filter size={16} />
                            Filtres
                            <ChevronDown size={16} className={`tavChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tavFilters">
                            <div className="tavField">
                                <label>Workflow</label>
                                <select
                                    value={filters.workflow}
                                    onChange={(e) => setFilters((p) => ({ ...p, workflow: e.target.value }))}
                                >
                                    <option value="">Tous</option>
                                    {workflows.map((w) => (
                                        <option key={w} value={w}>
                                            {w === "manager" ? "Manager seul" :
                                                w === "admin"   ? "RH seul" :
                                                    w === "both"    ? "Manager + RH" : w}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="tavField">
                                <label>Lieu</label>
                                <input
                                    type="text"
                                    value={filters.lieu}
                                    onChange={(e) => setFilters((p) => ({ ...p, lieu: e.target.value }))}
                                    placeholder="Casablanca, Rabat..."
                                />
                            </div>

                            <div className="tavField">
                                <label>Date début</label>
                                <input
                                    type="date"
                                    value={filters.dateDebut}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateDebut: e.target.value }))}
                                />
                            </div>

                            <div className="tavField">
                                <label>Date fin</label>
                                <input
                                    type="date"
                                    value={filters.dateFin}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateFin: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="tavCard tavCard--noPad">
                    <div className="tavTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tavTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tavCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tavTableWrap">
                        {filteredDemandes.length === 0 ? (
                            <div className="tavEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tavTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Mission</th>
                                    <th>Période</th>
                                    <th>Lieu</th>
                                    <th>Date demande</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredDemandes.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        className={demande.urgent ? "tavRowUrgent" : ""}
                                        onClick={() => showDetails(demande)}
                                        title={demande.urgent ? "Demande urgente" : ""}
                                    >
                                        <td>
                                            <span className="tavPrimary">{demande.employeeMatricule || "N/A"}</span>
                                        </td>
                                        <td>
                                            <div className="tavName">
                                                <span className="tavPrimary">{demande.employeeName}</span>
                                                {demande.urgent && <span className="tavFire">🔥</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="tavType">
                                                <Briefcase size={16} />
                                                <div>
                                                    <div className="tavPrimary">{demande.ordreMissionLibelle}</div>
                                                    <div className="tavSub">{demande.ordreMissionCode}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="tavPeriod">
                                                <div>{formatDate(demande.ordreMissionDateDebut)}</div>
                                                <div>→ {formatDate(demande.ordreMissionDateFin)}</div>
                                            </div>
                                        </td>
                                        <td>
                                            {demande.ordreMissionLieu ? (
                                                <div className="tavLocation">
                                                    <MapPin size={14} />
                                                    {demande.ordreMissionLieu}
                                                </div>
                                            ) : "—"}
                                        </td>
                                        <td>
                                            <span className="tavMuted">{formatDate(demande.createdAt)}</span>
                                        </td>
                                        <td>{getStatutBadge(demande.status)}</td>
                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            {activeTab === "enCours" ? (
                                                <div className="tavRowActions">
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn tavActionBtn--ok"
                                                        onClick={() => handleApprove(demande.id)}
                                                        title="Approuver"
                                                        disabled={loading}
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn tavActionBtn--no"
                                                        onClick={() => handleReject(demande)}
                                                        title="Refuser"
                                                        disabled={loading}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="tavDetailsBtn"
                                                    onClick={() => showDetails(demande)}
                                                >
                                                    Détails
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* MODAL DÉTAILS */}
            {showDetailsModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon">
                                    <Briefcase size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Demande de mission #{selectedDemande.id}</div>
                                    <div className="tavModal__sub">Demandée le {formatDate(selectedDemande.createdAt)}</div>
                                </div>
                            </div>
                            <button type="button" className="tavIconBtn" onClick={closeDetails}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavGrid2">
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <User size={16} /> Collaborateur
                                    </div>
                                    <div className="tavKv"><span>Matricule</span><b>{selectedDemande.employeeMatricule || "—"}</b></div>
                                    <div className="tavKv"><span>Nom</span><b>{selectedDemande.employeeName}</b></div>
                                </div>

                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <Briefcase size={16} /> Mission
                                    </div>
                                    <div className="tavKv"><span>Libellé</span><b>{selectedDemande.ordreMissionLibelle}</b></div>
                                    <div className="tavKv"><span>Code</span><b>{selectedDemande.ordreMissionCode}</b></div>
                                    <div className="tavKv">
                                        <span>Période</span>
                                        <b>{formatDate(selectedDemande.ordreMissionDateDebut)} → {formatDate(selectedDemande.ordreMissionDateFin)}</b>
                                    </div>
                                    {selectedDemande.ordreMissionLieu && (
                                        <div className="tavKv"><span>Lieu</span><b>{selectedDemande.ordreMissionLieu}</b></div>
                                    )}
                                    <div className="tavKv">
                                        <span>Workflow</span>
                                        <b>
                                            {selectedDemande.ordreMissionWorkflow === "both" ? "Manager + RH" :
                                                selectedDemande.ordreMissionWorkflow === "manager" ? "Manager" : "RH"}
                                        </b>
                                    </div>
                                </div>

                                <div className="tavPanel tavPanel--full">
                                    <div className="tavPanel__title">
                                        <FileText size={16} /> Motif de la demande
                                    </div>
                                    <div className="tavText">{selectedDemande.motif || "Non précisé"}</div>
                                </div>

                                {selectedDemande.managerDecision !== null && (
                                    <div className="tavPanel tavPanel--full">
                                        <div className="tavPanel__title">
                                            <Check size={16} /> Décision Manager
                                        </div>
                                        <div className="tavKv">
                                            <span>Décision</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && (
                                            <div className="tavKv"><span>Par</span><b>{selectedDemande.managerName}</b></div>
                                        )}
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="tavKv"><span>Date</span><b>{formatDate(selectedDemande.managerDecisionAt)}</b></div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="tavRejectBox">
                                                <div className="tavRejectLabel">Commentaire</div>
                                                <div className="tavRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="tavModal__footer">
                            {selectedDemande.status === "PENDING_MANAGER" ? (
                                <>
                                    <button
                                        className="tavBtn tavBtn--danger"
                                        onClick={() => { closeDetails(); handleReject(selectedDemande); }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>
                                    <button
                                        className="tavBtn tavBtn--ok"
                                        onClick={() => { handleApprove(selectedDemande.id); closeDetails(); }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>
                                    <button className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL REJET */}
            {showRejectModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel tavModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon tavModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Refuser la demande de mission</div>
                                    <div className="tavModal__sub">
                                        {selectedDemande.employeeName} — {selectedDemande.ordreMissionLibelle}
                                    </div>
                                </div>
                            </div>
                            <button type="button" className="tavIconBtn" onClick={closeReject}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavMiniCard">
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Mission</span>
                                    <b>{selectedDemande.ordreMissionCode} – {selectedDemande.ordreMissionLibelle}</b>
                                </div>
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Période</span>
                                    <b>{formatDate(selectedDemande.ordreMissionDateDebut)} → {formatDate(selectedDemande.ordreMissionDateFin)}</b>
                                </div>
                            </div>

                            <label className="tavLabel">
                                Motif du refus <span className="tavReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Expliquez pourquoi cette demande est refusée..."
                                className="tavTextarea"
                            />
                            <div className="tavHelp">Ce motif sera visible par le collaborateur</div>
                        </div>

                        <div className="tavModal__footer">
                            <button type="button" className="tavBtn tavBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tavBtn tavBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le refus"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementMissionsManager;